import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ArrowDownIcon } from 'lucide-react';
import { WINE_ADDON, WINE_FLIGHTS, type WineFlight } from '../../data/wine';
type Props = {
  selectedFlight: WineFlight['id'] | null;
  addonSelected: boolean;
  onSelectFlight: (id: WineFlight['id']) => void;
  onToggleAddon: () => void;
};
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1]
    }
  }
};
export function WineFlights({
  selectedFlight,
  addonSelected,
  onSelectFlight,
  onToggleAddon
}: Props) {
  return <section id="vinbrickor" className="w-full px-6 py-24 md:py-32 border-t border-ink/10" aria-labelledby="vinbrickor-rubrik" data-id="element-196" data-mp="2u965zmoevhiq">
      <div className="max-w-6xl mx-auto" data-id="element-197" data-mp="2qb04ou6cdu57">
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-80px'
      }} variants={{
        visible: {
          transition: {
            staggerChildren: 0.1
          }
        }
      }} data-id="element-198" data-mp="349gkdam6muzh">
          {/* Header */}
          <motion.div variants={fadeUp} className="max-w-2xl" data-id="element-199" data-mp="12s9oe928580q">
            <h2 id="vinbrickor-rubrik" className="font-sans text-5xl md:text-7xl font-medium tracking-[-0.04em] uppercase leading-[0.88] text-ink" data-id="element-200" data-mp="17q1d6u7koe0p">
              Vinbrickor
            </h2>
            <p className="mt-6 font-serif italic text-2xl md:text-3xl font-light text-ink/80" data-id="element-201" data-mp="1gnts3x9dtjnk">
              Vill du testa flera? Ta en vinbricka.
            </p>
            <p className="mt-6 font-sans text-sm md:text-base leading-relaxed text-ink/60" data-id="element-202" data-mp="1qn2lop0c4w9e">
              Prova flera olika viner och hitta dina favoriter. Våra vinbrickor
              serveras med cirka 8–10 cl per vin och är perfekta att dela på
              eller prova själv.
            </p>
            <a href="#vin-pa-glas" className="group mt-8 inline-flex items-center gap-2 font-sans text-[11px] md:text-xs tracking-[0.2em] uppercase text-ink/70 hover:text-ink transition-colors duration-200" data-id="element-203" data-mp="24gk2i21mzuvq">
              <span className="border-b border-ink/30 group-hover:border-ink transition-colors duration-200" data-id="element-204" data-mp="99ztmm6ddhb7">
                Se vinlistan
              </span>
              <ArrowDownIcon strokeWidth={1.5} className="w-3.5 h-3.5" data-id="element-205" data-mp="j2xfn2akloek" />
            </a>
          </motion.div>

          {/* Flights */}
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6" data-id="element-206" data-mp="e9m80yxxp3ll">
            {WINE_FLIGHTS.map(flight => {
            const isSelected = selectedFlight === flight.id;
            return <motion.div key={flight.id} variants={fadeUp} className={`flex flex-col p-8 md:p-10 border transition-colors duration-200 ${isSelected ? 'border-ink bg-ink/[0.03]' : 'border-ink/15 hover:border-ink/40'}`} data-id="element-207" data-mp="120rongbp7zwy">
                  <div className="flex items-start justify-between gap-4" data-id="element-208" data-mp="2yvo3h5mwpjda">
                    <span className="font-sans text-[10px] tracking-[0.25em] uppercase text-ink/45" data-id="element-209" data-mp="1ebsnfgrmjyak">
                      Nivå {flight.level}
                    </span>
                    {isSelected && <span className="inline-flex items-center gap-1.5 font-sans text-[10px] tracking-[0.2em] uppercase text-ink" data-id="element-210" data-mp="15ip7n1tib9cr">
                        <CheckIcon strokeWidth={2} className="w-3.5 h-3.5" data-id="element-211" data-mp="vl19s30ltlqd" />
                        Vald
                      </span>}
                  </div>

                  <h3 className="mt-6 font-sans text-3xl md:text-[2.75rem] font-medium tracking-[-0.03em] uppercase leading-[0.95] text-ink" data-id="element-212" data-mp="10hhx4uzzbw2e">
                    {flight.name}
                  </h3>

                  <p className="mt-3 font-sans text-[11px] md:text-xs tracking-[0.15em] uppercase text-ink/50" data-id="element-213" data-mp="qnhvj33wp8yf">
                    {flight.count} · {flight.servings}
                  </p>

                  <p className="mt-6 font-serif text-5xl md:text-6xl font-light text-ink tabular-nums" data-id="element-214" data-mp="2kxlhvl7dw2nl">
                    {flight.priceLabel}
                  </p>
                  <p className="mt-1 font-sans text-[11px] tracking-[0.15em] uppercase text-ink/40" data-id="element-215" data-mp="1rba09szcquv8">
                    per person
                  </p>

                  <p className="mt-6 font-sans text-sm leading-relaxed text-ink/60" data-id="element-216" data-mp="1hbsw5fzr0p1d">
                    {flight.description}
                  </p>

                  <div className="mt-auto pt-10" data-id="element-217" data-mp="1daz88mmb3a8h">
                    <button type="button" onClick={() => onSelectFlight(flight.id)} className={`w-full inline-flex items-center justify-center px-6 py-4 rounded-full font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase transition-colors duration-200 ${isSelected ? 'bg-ink text-white hover:bg-ink/85' : 'border border-ink/25 text-ink hover:border-ink'}`} data-id="element-218" data-mp="18jh4jqswevjt">
                      {isSelected ? 'Vald — ändra i bokningen' : flight.cta}
                    </button>
                  </div>
                </motion.div>;
          })}
          </div>

          {/* Add-on */}
          <motion.div variants={fadeUp} className={`mt-4 md:mt-6 p-8 md:p-10 border transition-colors duration-200 ${addonSelected ? 'border-ink bg-ink text-white' : 'border-ink/25 bg-ink/[0.02] hover:border-ink/50'}`} data-id="element-219" data-mp="3a6k0d6oa8s2o">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-12 items-end" data-id="element-220" data-mp="2ri5cgs99hu63">
              <div data-id="element-221" data-mp="2w8vgo25ybqz7">
                <div className="flex flex-wrap items-center gap-2.5" data-id="element-222" data-mp="2w9do7uy2drmf">
                  <span className={`font-sans text-[10px] tracking-[0.25em] uppercase ${addonSelected ? 'text-white/60' : 'text-ink/45'}`} data-id="element-223" data-mp="2waosdvvf91e2">
                    Nivå {WINE_ADDON.level}
                  </span>
                  <span className={`px-2.5 py-1 rounded-full font-sans text-[9px] tracking-[0.2em] uppercase ${addonSelected ? 'bg-white/15 text-white/80' : 'bg-ink/[0.07] text-ink/60'}`} data-id="element-224" data-mp="1vpscowx4tdn8">
                    Endast som tillval
                  </span>
                  <span className={`px-2.5 py-1 rounded-full border font-sans text-[9px] tracking-[0.2em] uppercase ${addonSelected ? 'border-white/30 text-white/70' : 'border-ink/30 text-ink/60'}`} data-id="element-225" data-mp="1ctakkeedfbqg">
                    {WINE_ADDON.label}
                  </span>
                </div>

                <h3 className={`mt-6 font-sans text-3xl md:text-[2.75rem] font-medium tracking-[-0.03em] uppercase leading-[0.95] ${addonSelected ? 'text-white' : 'text-ink'}`} data-id="element-226" data-mp="2y86l9u29u7ew">
                  {WINE_ADDON.name}
                </h3>

                <p className={`mt-3 font-sans text-[11px] md:text-xs tracking-[0.15em] uppercase ${addonSelected ? 'text-white/60' : 'text-ink/50'}`} data-id="element-227" data-mp="5ytb98pkupz0">
                  {WINE_ADDON.count} · {WINE_ADDON.servings}
                </p>

                <p className={`mt-6 max-w-xl font-sans text-sm leading-relaxed ${addonSelected ? 'text-white/70' : 'text-ink/60'}`} data-id="element-228" data-mp="12ou8lw40ierx">
                  {WINE_ADDON.description}
                </p>

                <p className={`mt-6 font-sans text-[11px] leading-relaxed ${addonSelected ? 'text-white/55' : 'text-ink/45'}`} data-id="element-229" data-mp="tunuwpa5mn4p">
                  Går inte att boka på egen hand — läggs till på Något Gott
                  eller Något Extra.
                </p>
              </div>

              <div className="flex flex-col items-start lg:items-end gap-6 shrink-0" data-id="element-230" data-mp="3dog7vt03rrjr">
                <div className="lg:text-right" data-id="element-231" data-mp="38pmtlyczrwij">
                  <p className={`font-serif text-5xl md:text-6xl font-light tabular-nums ${addonSelected ? 'text-white' : 'text-ink'}`} data-id="element-232" data-mp="38obpfxfmwmqw">
                    {WINE_ADDON.priceLabel}
                  </p>
                  <p className={`mt-1 font-sans text-[11px] tracking-[0.15em] uppercase ${addonSelected ? 'text-white/50' : 'text-ink/40'}`} data-id="element-233" data-mp="b4itovo22v8">
                    per person
                  </p>
                </div>
                <button type="button" onClick={onToggleAddon} className={`inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-sans text-[10px] md:text-xs tracking-[0.2em] uppercase transition-colors duration-200 ${addonSelected ? 'bg-white text-ink hover:bg-white/85' : 'border border-ink/25 text-ink hover:border-ink'}`} data-id="element-234" data-mp="2g3ad6k92wfnj">
                  {addonSelected ? <>
                      <CheckIcon strokeWidth={2} className="w-3.5 h-3.5" data-id="element-235" data-mp="1a9dwh1h38c1v" />
                      Tillagd
                    </> : WINE_ADDON.cta}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}